import { Icon } from '@iconify/react';
import { styled } from '@linaria/react';
import { Tooltip } from '@mantine/core';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type {
  ArtifactSpec,
  ChatHistory,
  Document,
  Message,
  TokenUsage,
  ToolApproval,
  ToolApprovalDecision,
} from '../../flora/flora.type';
import type { GoogleFileLink } from '../hooks/use-sheet-access-gate';
import { FloraPageRailPanel } from '../../flora-pages/components/flora-page-rail-panel';
import { ArtifactExportProvider } from '../artifacts/artifact-export-context';
import { AgentArtifactCard } from './agent-artifact-card';
import { AgentFilesMenu } from './agent-files-menu';
import { AgentChatExportButton } from './agent-chat-export/agent-chat-export-button';
import { AgentInput } from '../../flora/components/chat/agent-input';
import { AgentMessageList } from '../../flora/components/chat/agent-message-list';
import { deriveTodoBarState } from '../../flora/components/flora-todo-bar/flora-todo-bar.helpers';
import type { AgentAttachmentsBinding } from '../../flora/components/chat/use-agent-attachments';
import { DocPreviewRailPanel } from './doc-preview-rail-panel';
import { GoogleDocViewer } from './google-doc-embed/google-doc-viewer';

const MIN_ARTIFACT_WIDTH = 280;
const DEFAULT_ARTIFACT_WIDTH = 420;
const EXPORT_ARTIFACT_WIDTH = 700;
// File previews (documents, tables, Google files) need more room than a chart
// card, so they open at a share of the chat area, within these bounds.
const FILE_PANEL_WIDTH_RATIO = 0.55;
const MIN_FILE_PANEL_WIDTH = 560;
const FALLBACK_FILE_PANEL_WIDTH = 720;
const MAX_RAIL_WIDTH_RATIO = 0.7;

/**
 * Picks the opening width for a file preview panel: a share of the chat area,
 * no narrower than a readable minimum and never wider than the drag limit.
 *
 * @param {number} containerWidth - The chat content area's width in pixels (0 when unmeasured)
 * @returns {number} The panel width in pixels
 */
function defaultFilePanelWidth(containerWidth: number): number {
  if (containerWidth <= 0) return FALLBACK_FILE_PANEL_WIDTH;
  const preferred = Math.max(containerWidth * FILE_PANEL_WIDTH_RATIO, MIN_FILE_PANEL_WIDTH);
  return Math.round(Math.min(preferred, containerWidth * MAX_RAIL_WIDTH_RATIO));
}

interface AgentChatViewProps {
  questions: Message[];
  messages: Message[];
  isWaitingForFirstResponse: boolean;
  isActiveQuestion: boolean;
  loadingMessage: string;
  question: string;
  onQuestionChange: (value: string) => void;
  onSubmit: (formattedMessage?: string, displayMessage?: string) => void;
  onNewChat: () => void;
  chatHistories: ChatHistory[];
  threadId: string;
  latestTurnIsLive?: boolean;
  tokenUsage: TokenUsage | null;
  dataTheme?: string;
  chatName?: string;
  artifactPanelOpen: boolean;
  onToggleArtifactPanel: () => void;
  artifacts: ArtifactSpec[];
  documentPanelOpen: boolean;
  /** Opens the documents rail on a Google file chosen from the Files menu. */
  onOpenGoogleFile: (fileId: string) => void;
  /** Records a Google file switch made inside the documents rail. */
  onSelectGoogleFile?: (fileId: string) => void;
  selectedGoogleFileId?: string;
  /** Closes whichever file panel (Flora preview or Google) is open. */
  onCloseFilesPanel: () => void;
  documents: GoogleFileLink[];
  documentRevisions: Record<string, number>;
  pagePanelOpen: boolean;
  onTogglePagePanel: () => void;
  hasPages: boolean;
  selectedPageId: string | undefined;
  docPreviewPanelOpen: boolean;
  onPreviewDocument?: (documentId: string) => void;
  onSelectPreviewDoc?: (documentId: string) => void;
  previewableDocs: Document[];
  selectedPreviewDocId?: string;
  onFollowupClick?: (prompt: string) => void;
  onAnswerSubmit?: (jobId: string, answers: Record<string, string | string[]>) => void;
  onToolApproval?: (approvalDecision: ToolApprovalDecision, toolApproval: ToolApproval) => void;
  attachments: AgentAttachmentsBinding;
  onStop?: () => void;
}

export function AgentChatView({
  questions,
  messages,
  isWaitingForFirstResponse,
  isActiveQuestion,
  loadingMessage,
  question,
  onQuestionChange,
  onSubmit,
  threadId,
  latestTurnIsLive,
  tokenUsage,
  dataTheme,
  chatName,
  artifactPanelOpen,
  onToggleArtifactPanel,
  artifacts,
  documentPanelOpen,
  onOpenGoogleFile,
  onSelectGoogleFile,
  selectedGoogleFileId,
  onCloseFilesPanel,
  documents,
  documentRevisions,
  pagePanelOpen,
  onTogglePagePanel,
  hasPages,
  selectedPageId,
  docPreviewPanelOpen,
  onPreviewDocument,
  onSelectPreviewDoc,
  previewableDocs,
  selectedPreviewDocId,
  onFollowupClick,
  onAnswerSubmit,
  onToolApproval,
  attachments,
  onStop,
}: AgentChatViewProps) {
  const hasArtifacts = artifacts.length > 0;
  const hasDocuments = documents.length > 0;
  const hasPreviewableDocs = previewableDocs.length > 0;
  // The rail is visible when a panel is toggled open AND has content to show.
  const railOpen =
    (artifactPanelOpen && hasArtifacts) ||
    (documentPanelOpen && hasDocuments) ||
    (pagePanelOpen && hasPages) ||
    (docPreviewPanelOpen && hasPreviewableDocs);
  const todoBar = useMemo(() => deriveTodoBarState(messages, questions), [messages, questions]);
  // A prior turn's unfinished plan still shows, but only the current turn's animates.
  const todosAreLive = isActiveQuestion && todoBar.isCurrentTurn;

  const contentAreaRef = useRef<HTMLDivElement>(null);
  // Charts and file previews keep separate widths, so widening a document
  // preview does not stretch the chart cards (and vice versa). The file width
  // is sized from the chat area the first time a file panel opens.
  const [chartWidth, setChartWidth] = useState(DEFAULT_ARTIFACT_WIDTH);
  const [fileWidth, setFileWidth] = useState<number | null>(null);
  const filePanelShown = (docPreviewPanelOpen && hasPreviewableDocs) || (documentPanelOpen && hasDocuments);
  useLayoutEffect(() => {
    if (!filePanelShown || fileWidth !== null) return;
    setFileWidth(defaultFilePanelWidth(contentAreaRef.current?.getBoundingClientRect().width ?? 0));
  }, [filePanelShown, fileWidth]);
  const artifactWidth = filePanelShown ? (fileWidth ?? FALLBACK_FILE_PANEL_WIDTH) : chartWidth;
  const setArtifactWidth = filePanelShown ? setFileWidth : setChartWidth;
  const [isDragging, setIsDragging] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!contentAreaRef.current) return;
      const containerRect = contentAreaRef.current.getBoundingClientRect();
      const maxWidth = containerRect.width * MAX_RAIL_WIDTH_RATIO;
      const newWidth = containerRect.right - e.clientX;
      setArtifactWidth(Math.max(MIN_ARTIFACT_WIDTH, Math.min(newWidth, maxWidth)));
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, setArtifactWidth]);

  return (
    <ViewContainer>
      <ContentArea ref={contentAreaRef}>
        <MessagesColumn>
          <AgentMessageList
            questions={questions}
            messages={messages}
            isWaitingForFirstResponse={isWaitingForFirstResponse}
            isActiveQuestion={isActiveQuestion}
            loadingMessage={loadingMessage}
            chatName={chatName}
            chatHistoryId={threadId}
            latestTurnIsLive={latestTurnIsLive}
            enableTurnRail={!railOpen}
            onPreviewDocument={onPreviewDocument}
            onFollowupClick={onFollowupClick}
            onAnswerSubmit={onAnswerSubmit}
            onToolApproval={onToolApproval}
            themeToggle={
              questions.length > 0 ? (
                <ToolbarGroup>
                  {hasPages && (
                    <Tooltip label={pagePanelOpen ? 'Close page' : 'Open page'} position="bottom" withArrow>
                      <ArtifactPanelToggle
                        onClick={onTogglePagePanel}
                        type="button"
                        aria-label={pagePanelOpen ? 'Close page panel' : 'Open page panel'}
                        aria-pressed={pagePanelOpen}
                        data-active={pagePanelOpen}
                      >
                        <Icon
                          icon={pagePanelOpen ? 'material-symbols:close' : 'material-symbols:article-outline'}
                          width={16}
                          height={16}
                        />
                      </ArtifactPanelToggle>
                    </Tooltip>
                  )}
                  <AgentFilesMenu
                    floraDocuments={previewableDocs}
                    googleFiles={documents}
                    activeFloraDocumentId={
                      docPreviewPanelOpen ? (selectedPreviewDocId ?? previewableDocs[0]?.document_id) : undefined
                    }
                    activeGoogleFileId={documentPanelOpen ? (selectedGoogleFileId ?? documents[0]?.fileId) : undefined}
                    panelOpen={(documentPanelOpen && hasDocuments) || (docPreviewPanelOpen && hasPreviewableDocs)}
                    onOpenFloraDocument={(documentId) => onPreviewDocument?.(documentId)}
                    onOpenGoogleFile={onOpenGoogleFile}
                    onClosePanel={onCloseFilesPanel}
                  />
                  {hasArtifacts && (
                    <Tooltip
                      label={artifactPanelOpen ? 'Close artifacts' : 'Open artifacts'}
                      position="bottom"
                      withArrow
                    >
                      <ArtifactPanelToggle
                        onClick={onToggleArtifactPanel}
                        type="button"
                        aria-label={artifactPanelOpen ? 'Close artifacts panel' : 'Open artifacts panel'}
                        aria-pressed={artifactPanelOpen}
                        data-active={artifactPanelOpen}
                      >
                        <Icon
                          icon={
                            artifactPanelOpen ? 'material-symbols:close' : 'material-symbols:bar-chart-4-bars-rounded'
                          }
                          width={16}
                          height={16}
                        />
                      </ArtifactPanelToggle>
                    </Tooltip>
                  )}
                  <AgentChatExportButton
                    questions={questions}
                    messages={messages}
                    chatName={chatName}
                    disabled={isActiveQuestion}
                    artifactPanelOpen={artifactPanelOpen}
                    onOpenArtifactPanel={onToggleArtifactPanel}
                    onExportingChange={setIsExporting}
                  />
                </ToolbarGroup>
              ) : undefined
            }
          />
        </MessagesColumn>

        {railOpen && (
          <>
            <DividerHandle onMouseDown={handleMouseDown} data-dragging={isDragging}>
              <DragDots>
                <Icon icon="material-symbols:drag-indicator" width={14} height={14} />
              </DragDots>
            </DividerHandle>

            <ArtifactsColumn
              style={{
                width: isExporting ? EXPORT_ARTIFACT_WIDTH : artifactWidth,
                minWidth: isExporting ? EXPORT_ARTIFACT_WIDTH : artifactWidth,
              }}
              $isOpen={railOpen}
            >
              {pagePanelOpen ? (
                <FloraPageRailPanel pageId={selectedPageId} />
              ) : docPreviewPanelOpen ? (
                <DocPreviewRailPanel
                  documents={previewableDocs}
                  selectedDocumentId={selectedPreviewDocId}
                  onSelectDocument={onSelectPreviewDoc}
                />
              ) : documentPanelOpen ? (
                <GoogleDocViewer
                  documents={documents}
                  documentRevisions={documentRevisions}
                  selectedFileId={selectedGoogleFileId}
                  onSelectFile={onSelectGoogleFile}
                />
              ) : (
                <ArtifactExportProvider value={isExporting}>
                  <ArtifactsPanelBody>
                    {artifacts.map((artifact) => (
                      <AgentArtifactCard key={artifact.id} artifact={artifact} />
                    ))}
                  </ArtifactsPanelBody>
                </ArtifactExportProvider>
              )}
            </ArtifactsColumn>
          </>
        )}
      </ContentArea>

      <AgentInput
        question={question}
        onQuestionChange={onQuestionChange}
        onSubmit={onSubmit}
        isActiveQuestion={isActiveQuestion}
        tokenUsage={tokenUsage}
        dataTheme={dataTheme}
        attachments={attachments}
        onStop={onStop}
        threadId={threadId}
        todos={todoBar.todos}
        todosLive={todosAreLive}
      />

      {/* While dragging, cover the view so sandboxed iframes in the rail (doc
          preview, page embed, Google Docs viewer) can't swallow the
          mousemove/mouseup — otherwise the resize stalls the moment the cursor
          crosses onto one and the drag never releases. */}
      {isDragging && <ResizeShield data-testid="resize-shield" />}
    </ViewContainer>
  );
}

const ViewContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
  min-height: 0;
  min-width: 0;
  background: var(--base-white);
  position: relative;
`;

const ContentArea = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  min-height: 0;
`;

/* Transparent capture layer shown only during a resize drag, above any rail
   iframe so pointer events reach the window listeners that drive the resize. */
const ResizeShield = styled.div`
  position: absolute;
  inset: 0;
  z-index: 10;
  cursor: col-resize;
`;

const MessagesColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  min-height: 0;
`;

const DividerHandle = styled.div`
  width: 5px;
  background: var(--sky-light);
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: col-resize;
  transition: background 0.15s;

  &::before {
    content: '';
    position: absolute;
    inset: 0 -3px;
  }

  &:hover,
  &[data-dragging='true'] {
    background: var(--secondary-light);
  }

  /* Highlight the nested drag-dots (the styled.span child) while this handle
     is hovered or dragging. We target the child span directly instead of
     interpolating the DragDots styled component as a selector: wyw-in-js@2
     (Oxc) tree-shakes a styled component whose only reference is such an
     interpolation, dropping its declaration while keeping its JSX usage and
     causing a "ReferenceError: ... is not defined" at render. */
  &:hover > span,
  &[data-dragging='true'] > span {
    border-color: var(--secondary-light);
    color: var(--secondary-base);
  }
`;

const DragDots = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--ink-lighter);
  background: var(--base-white);
  border: 1px solid var(--sky-light);
  border-radius: 4px;
  padding: 2px 0;
  pointer-events: none;
  transition: border-color 0.15s, color 0.15s, background 0.15s;
`;

const ArtifactsColumn = styled.div<{ $isOpen: boolean }>`
  width: ${({ $isOpen }) => ($isOpen ? `${DEFAULT_ARTIFACT_WIDTH}px` : '0px')};
  min-width: ${({ $isOpen }) => ($isOpen ? `${MIN_ARTIFACT_WIDTH}px` : '0px')};
  overflow: hidden;
  transition: ${({ $isOpen }) => ($isOpen ? 'none' : 'width 0.25s ease, min-width 0.25s ease')};
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const ArtifactsPanelBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: var(--sky-lightest);
`;

const ToolbarGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ArtifactPanelToggle = styled.button`
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid var(--sky-light);
  background: var(--base-white);
  color: var(--ink-light);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;

  &:hover {
    border-color: var(--sky-base);
    color: var(--ink-darkest);
    background: var(--sky-lighter);
  }

  &[data-active='true'] {
    border-color: var(--secondary-base);
    color: var(--secondary-base);
    background: var(--secondary-lightest);
  }
`;
